import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GameStateResponse } from '@/lib/api';

interface UseGameStateReturn {
  gameState: GameStateResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGameState(keyword: string): UseGameStateReturn {
  const [gameState, setGameState] = useState<GameStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchState = useCallback(async () => {
    if (!keyword) {
      setLoading(false);
      return;
    }
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameState(keyword);
      if (fetchIdRef.current !== id) return;
      setGameState(result.gameState);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load game state');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [keyword]);

  const refresh = useCallback(() => {
    setError(null);
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { gameState, loading, error, refresh };
}
